import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ExternalLink,
  LogIn,
  Monitor,
  PanelRightOpen,
  ShieldAlert,
  SquareArrowOutUpRight,
  Users,
  UserPlus,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { orgUsersApi } from '@/api/orgUsers';
import { Avatar, Badge, Button, EmptyState, Loading, Modal } from '@/components/ui';
import { cn } from '@/utils/helpers';
import type { OrgUser } from '@/types';

interface Props {
  orgId: string;
  orgName: string;
}

type DisplayMode = 'popup' | 'embedded';

/** Reused so each "Login as" click replaces the previous session in the same popup. */
const IMPERSONATION_WINDOW_NAME = 'otomator-impersonate';
const IMPERSONATION_WINDOW_FEATURES =
  'width=1200,height=820,left=200,top=80,resizable=yes,scrollbars=yes';
const DISPLAY_MODE_STORAGE_KEY = 'otomator-admin:impersonate-display-mode';

function roleBadgeVariant(role: string): 'success' | 'warning' | 'neutral' {
  if (role === 'owner') return 'warning';
  if (role === 'admin') return 'success';
  return 'neutral';
}

function splitName(full: string | null, email: string): { first: string; last: string } {
  const source = full && full.trim().length > 0 ? full : email;
  const [first = '', ...rest] = source.trim().split(/\s+/);
  return { first, last: rest.join(' ') };
}

function loadInitialMode(): DisplayMode {
  if (typeof window === 'undefined') return 'popup';
  return window.localStorage.getItem(DISPLAY_MODE_STORAGE_KEY) === 'embedded'
    ? 'embedded'
    : 'popup';
}

export default function OrgUsersTab({ orgId, orgName }: Props) {
  const queryClient = useQueryClient();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invite, setInvite] = useState({ email: '', first_name: '', last_name: '', role_slug: 'staff', reason: '' });
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const [mode, setMode] = useState<DisplayMode>(loadInitialMode);
  const [activeUser, setActiveUser] = useState<OrgUser | null>(null);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);

  const { data: users, isLoading } = useQuery({
    queryKey: ['org-users-admin', orgId],
    queryFn: () => orgUsersApi.list(orgId),
  });

  const inviteMutation = useMutation({
    mutationFn: () => orgUsersApi.invite({
      organization_id: orgId,
      email: invite.email.trim(),
      first_name: invite.first_name.trim() || undefined,
      last_name: invite.last_name.trim() || undefined,
      role_slug: invite.role_slug as 'admin' | 'manager' | 'marketing_manager' | 'staff' | 'viewer',
      reason: invite.reason.trim(),
    }),
    onSuccess: () => {
      toast.success('ההזמנה נשלחה והפעולה נרשמה ביומן');
      void queryClient.invalidateQueries({ queryKey: ['org-users-admin', orgId] });
      setInviteOpen(false);
      setInvite({ email: '', first_name: '', last_name: '', role_slug: 'staff', reason: '' });
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : 'הוספת המשתמש נכשלה'),
  });

  const changeMode = (next: DisplayMode) => {
    setMode(next);
    window.localStorage.setItem(DISPLAY_MODE_STORAGE_KEY, next);
    if (next === 'popup') {
      setActiveUser(null);
      setIframeUrl(null);
    }
  };

  const closeEmbedded = () => {
    setActiveUser(null);
    setIframeUrl(null);
  };

  const loginAs = async (user: OrgUser) => {
    setPendingUserId(user.user_id);
    try {
      const { url } = await orgUsersApi.impersonate(orgId, user.user_id);
      if (mode === 'embedded') {
        setActiveUser(user);
        setIframeUrl(url);
        toast.success(`מציג כ־${user.full_name ?? user.email}`);
      } else {
        const popup = window.open(url, IMPERSONATION_WINDOW_NAME, IMPERSONATION_WINDOW_FEATURES);
        if (!popup) {
          toast.error('הדפדפן חסם את חלון הקופץ — אפשר Pop-ups עבור דומיין זה');
          return;
        }
        popup.focus();
        toast.success(`נפתח חלון התחברות בשם ${user.full_name ?? user.email}`);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'שגיאה לא ידועה';
      toast.error(`כניסה נכשלה: ${msg}`);
    } finally {
      setPendingUserId(null);
    }
  };

  const openActiveInNewTab = () => {
    if (iframeUrl) window.open(iframeUrl, '_blank', 'noopener,noreferrer');
  };

  const modeLabel = mode === 'embedded' ? 'מוטמע (iframe)' : 'חלון חיצוני';

  return (
    <div dir="rtl" className="space-y-4">
      <Modal
        isOpen={inviteOpen}
        onClose={() => setInviteOpen(false)}
        title={`הוספת משתמש · ${orgName}`}
        footer={(
          <>
            <Button variant="secondary" onClick={() => setInviteOpen(false)} disabled={inviteMutation.isPending}>ביטול</Button>
            <Button
              onClick={() => inviteMutation.mutate()}
              loading={inviteMutation.isPending}
              disabled={!invite.email.includes('@') || invite.reason.trim().length < 5}
            >שליחת הזמנה</Button>
          </>
        )}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-sm text-ink-secondary sm:col-span-2">אימייל
            <input dir="ltr" type="email" value={invite.email} onChange={(event) => setInvite((value) => ({ ...value, email: event.target.value }))} className="mt-1 w-full rounded-lg border border-navy-border bg-navy-dark px-3 py-2 text-ink-primary" />
          </label>
          <label className="text-sm text-ink-secondary">שם פרטי
            <input value={invite.first_name} onChange={(event) => setInvite((value) => ({ ...value, first_name: event.target.value }))} className="mt-1 w-full rounded-lg border border-navy-border bg-navy-dark px-3 py-2 text-ink-primary" />
          </label>
          <label className="text-sm text-ink-secondary">שם משפחה
            <input value={invite.last_name} onChange={(event) => setInvite((value) => ({ ...value, last_name: event.target.value }))} className="mt-1 w-full rounded-lg border border-navy-border bg-navy-dark px-3 py-2 text-ink-primary" />
          </label>
          <label className="text-sm text-ink-secondary">תפקיד
            <select value={invite.role_slug} onChange={(event) => setInvite((value) => ({ ...value, role_slug: event.target.value }))} className="mt-1 w-full rounded-lg border border-navy-border bg-navy-dark px-3 py-2 text-ink-primary">
              <option value="staff">צוות</option><option value="viewer">צפייה</option><option value="manager">מנהל</option><option value="marketing_manager">מנהל שיווק</option><option value="admin">אדמין</option>
            </select>
          </label>
          <label className="text-sm text-ink-secondary sm:col-span-2">סיבה לביצוע
            <textarea rows={2} value={invite.reason} onChange={(event) => setInvite((value) => ({ ...value, reason: event.target.value }))} placeholder="לפחות 5 תווים" className="mt-1 w-full rounded-lg border border-navy-border bg-navy-dark px-3 py-2 text-ink-primary" />
          </label>
          <p className="text-xs text-amber-300 sm:col-span-2">המכסה נבדקת בשרת. אם הארגון הגיע למכסת המשתמשים, ההזמנה תיחסם ותופיע כהזדמנות שדרוג במוניטור.</p>
        </div>
      </Modal>
      <div className="card p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-3">
            <ShieldAlert className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-ink-secondary">
              <p>
                לחיצה על <b className="text-ink-primary">"כניסה כמשתמש"</b> מייצרת קישור חד-פעמי
                לכניסה ל־<span className="num" dir="ltr">hub.jumpstart.co.il</span> כאותו משתמש.
                כל פעולה נרשמת ביומן ההשתלטות.
              </p>
              {mode === 'embedded' && (
                <p className="mt-2">
                  <b className="text-amber-300">שים לב:</b> מצב מוטמע דורש ש־hub ישלח כותרות{' '}
                  <span className="num" dir="ltr">CSP frame-ancestors</span>. אם המסך נשאר ריק —
                  עבור זמנית למצב חלון חיצוני.
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 self-start">
            <Button size="sm" onClick={() => setInviteOpen(true)}><UserPlus className="h-4 w-4" />הוספת משתמש</Button>
          <div className="flex items-center gap-1 rounded-lg bg-navy-dark border border-navy-border p-1">
            <button
              type="button"
              onClick={() => changeMode('popup')}
              className={cn(
                'flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md transition-colors',
                mode === 'popup'
                  ? 'bg-gold text-navy font-semibold'
                  : 'text-ink-secondary hover:text-ink-primary',
              )}
            >
              <SquareArrowOutUpRight className="h-3.5 w-3.5" />
              חלון חיצוני
            </button>
            <button
              type="button"
              onClick={() => changeMode('embedded')}
              className={cn(
                'flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md transition-colors',
                mode === 'embedded'
                  ? 'bg-gold text-navy font-semibold'
                  : 'text-ink-secondary hover:text-ink-primary',
              )}
            >
              <PanelRightOpen className="h-3.5 w-3.5" />
              מוטמע
            </button>
          </div>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="card p-6">
          <Loading text="טוען משתמשים..." />
        </div>
      ) : !users?.length ? (
        <div className="card p-8">
          <EmptyState
            icon={<Users className="h-10 w-10 text-slate-600" />}
            title="אין משתמשים לארגון"
            description={`לארגון ${orgName} אין משתמשים רשומים בטבלת org_users`}
          />
        </div>
      ) : mode === 'embedded' ? (
        <EmbeddedLayout
          users={users}
          activeUser={activeUser}
          iframeUrl={iframeUrl}
          pendingUserId={pendingUserId}
          onLoginAs={loginAs}
          onOpenInNewTab={openActiveInNewTab}
          onClose={closeEmbedded}
          modeLabel={modeLabel}
        />
      ) : (
        <PopupUsersTable
          users={users}
          pendingUserId={pendingUserId}
          onLoginAs={loginAs}
        />
      )}
    </div>
  );
}

/* ─── Popup mode: full-width table + mobile cards ──────────────────────── */

function PopupUsersTable({
  users,
  pendingUserId,
  onLoginAs,
}: {
  users: OrgUser[];
  pendingUserId: string | null;
  onLoginAs: (u: OrgUser) => void;
}) {
  return (
    <div className="card overflow-hidden">
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-navy-border text-ink-muted">
              <th className="text-right p-3 font-medium">משתמש</th>
              <th className="text-right p-3 font-medium">אימייל</th>
              <th className="text-right p-3 font-medium">תפקיד</th>
              <th className="text-right p-3 font-medium">פעולות</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.user_id} className="border-b border-navy-border/70 last:border-b-0">
                <td className="p-3">
                  <UserNameCell user={u} />
                </td>
                <td className="p-3 text-ink-secondary num" dir="ltr">{u.email}</td>
                <td className="p-3">
                  <Badge variant={roleBadgeVariant(u.role)}>{u.role}</Badge>
                </td>
                <td className="p-3">
                  <Button
                    size="sm"
                    loading={pendingUserId === u.user_id}
                    onClick={() => onLoginAs(u)}
                  >
                    <LogIn className="h-3.5 w-3.5" />
                    כניסה כמשתמש
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="lg:hidden p-3 space-y-3">
        {users.map((u) => (
          <div key={u.user_id} className="bg-navy-dark border border-navy-border rounded-xl p-3">
            <div className="flex items-center gap-3 mb-2">
              <UserNameCell user={u} />
              <Badge variant={roleBadgeVariant(u.role)}>{u.role}</Badge>
            </div>
            <Button
              size="sm"
              className="w-full"
              loading={pendingUserId === u.user_id}
              onClick={() => onLoginAs(u)}
            >
              <LogIn className="h-3.5 w-3.5" />
              כניסה כמשתמש
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Embedded mode: compact user list + iframe (AnyDesk-style) ────────── */

function EmbeddedLayout({
  users,
  activeUser,
  iframeUrl,
  pendingUserId,
  onLoginAs,
  onOpenInNewTab,
  onClose,
  modeLabel,
}: {
  users: OrgUser[];
  activeUser: OrgUser | null;
  iframeUrl: string | null;
  pendingUserId: string | null;
  onLoginAs: (u: OrgUser) => void;
  onOpenInNewTab: () => void;
  onClose: () => void;
  modeLabel: string;
}) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
      <div className="lg:col-span-4 card p-2 max-h-[75vh] overflow-y-auto">
        <div className="px-2 py-1.5 text-xs text-ink-muted">
          {users.length} משתמשים · מצב {modeLabel}
        </div>
        <ul className="space-y-1">
          {users.map((u) => {
            const isActive = activeUser?.user_id === u.user_id;
            return (
              <li key={u.user_id}>
                <button
                  type="button"
                  onClick={() => onLoginAs(u)}
                  disabled={pendingUserId === u.user_id}
                  className={cn(
                    'w-full text-right flex items-center gap-3 p-2 rounded-lg transition-colors',
                    'disabled:opacity-60 disabled:cursor-wait',
                    isActive
                      ? 'bg-gold/10 border border-gold/40'
                      : 'hover:bg-navy-hover border border-transparent',
                  )}
                >
                  <UserNameCell user={u} compact />
                  <Badge variant={roleBadgeVariant(u.role)}>{u.role}</Badge>
                  {isActive ? (
                    <Monitor className="h-4 w-4 text-gold flex-shrink-0" />
                  ) : (
                    <LogIn className="h-4 w-4 text-ink-muted flex-shrink-0" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="lg:col-span-8 card overflow-hidden flex flex-col min-h-[60vh] lg:min-h-[75vh]">
        {!activeUser || !iframeUrl ? (
          <div className="flex-1 flex items-center justify-center p-8">
            <EmptyState
              icon={<Monitor className="h-10 w-10 text-slate-600" />}
              title="בחר משתמש כדי להתחיל"
              description="לחיצה על משתמש ברשימה תפעיל סשן מוטמע של hub.jumpstart.co.il"
            />
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-navy-border bg-navy-dark">
              <div className="flex items-center gap-2 min-w-0">
                <Monitor className="h-4 w-4 text-gold flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs text-ink-muted">מציג כ־</p>
                  <p className="text-sm text-ink-primary truncate">
                    {activeUser.full_name ?? activeUser.email}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="ghost" onClick={onOpenInNewTab}>
                  <ExternalLink className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">פתח בחלון חדש</span>
                </Button>
                <Button size="sm" variant="ghost" onClick={onClose}>
                  <X className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">סגור</span>
                </Button>
              </div>
            </div>
            <iframe
              key={iframeUrl}
              src={iframeUrl}
              title={`hub.jumpstart.co.il as ${activeUser.email}`}
              className="flex-1 w-full bg-white"
              referrerPolicy="strict-origin"
              allow="clipboard-read; clipboard-write; storage-access"
            />
          </>
        )}
      </div>
    </div>
  );
}

/* ─── Shared bits ──────────────────────────────────────────────────────── */

function UserNameCell({ user, compact }: { user: OrgUser; compact?: boolean }) {
  const n = splitName(user.full_name, user.email);
  return (
    <div className={cn('flex items-center gap-3 min-w-0', compact && 'flex-1')}>
      <Avatar
        src={user.avatar_url ?? undefined}
        firstName={n.first}
        lastName={n.last}
        size="sm"
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm text-ink-primary truncate">{user.full_name ?? '—'}</p>
        {compact && (
          <p className="text-xs text-ink-muted num truncate" dir="ltr">{user.email}</p>
        )}
      </div>
    </div>
  );
}
